import { Card, CardContent } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Clock, User, CalendarClock } from "lucide-react";
import { MemberSchedule } from "@/hooks/schedules/useMemberSchedule";
import { ScheduleChangeRequest } from "@/hooks/schedules/useScheduleChangeRequests";
import { format } from "date-fns";
import { es } from "date-fns/locale";

const DAY_NAMES = ["Domingo", "Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado"];

interface Props {
  schedule: MemberSchedule;
  activeChange?: ScheduleChangeRequest | null;
}

export const ScheduleCard = ({ schedule, activeChange }: Props) => {
  const duration = (() => {
    const [sh, sm] = schedule.start_time.split(":").map(Number);
    const [eh, em] = schedule.end_time.split(":").map(Number);
    return (eh * 60 + em) - (sh * 60 + sm);
  })();

  const parseLocalDate = (dateStr: string) => {
    const [y, m, d] = dateStr.split("-").map(Number);
    return new Date(y, m - 1, d);
  };

  return (
    <Card className="overflow-hidden border-primary/20">
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center justify-between">
          <span className="text-lg font-semibold text-foreground">
            {DAY_NAMES[schedule.day_of_week]}
          </span>
          <div className="flex items-center gap-1 text-sm text-muted-foreground">
            <Clock className="w-4 h-4" />
            {schedule.start_time.slice(0, 5)} - {schedule.end_time.slice(0, 5)}
          </div>
        </div>

        <p className="text-xs text-muted-foreground">{duration} minutos</p>

        {/* Active weekly change indicator */}
        {activeChange && activeChange.replacement_date && (
          <div className="flex items-start gap-2 p-2 rounded-md bg-green-500/10 border border-green-500/20">
            <CalendarClock className="w-4 h-4 text-green-600 mt-0.5 shrink-0" />
            <div className="text-xs">
              <p className="font-medium text-green-700 dark:text-green-400">Cambio aprobado esta semana</p>
              <p className="text-green-600 dark:text-green-500">
                {DAY_NAMES[activeChange.requested_day]} {format(parseLocalDate(activeChange.replacement_date), "d/M", { locale: es })}{" "}
                {activeChange.requested_start_time.slice(0, 5)} - {activeChange.requested_end_time.slice(0, 5)}
              </p>
            </div>
          </div>
        )}

        {schedule.professor && (
          <div className="flex items-center gap-3 pt-2 border-t border-border/50">
            <Avatar className="h-9 w-9">
              <AvatarImage src={schedule.professor.avatar_url || ""} />
              <AvatarFallback className="bg-primary/10 text-primary text-xs">
                <User className="w-4 h-4" />
              </AvatarFallback>
            </Avatar>
            <div>
              <p className="text-xs text-muted-foreground">Profesor</p>
              <p className="text-sm font-medium text-foreground">
                {schedule.professor.first_name} {schedule.professor.last_name}
              </p>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
